import { useState, useEffect } from "react";
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, 
  Lightbulb, 
  Target, 
  Crown,
  Brain,
  Heart,
  Sparkles,
  Clock
} from "lucide-react";

export function ChessFactsSection() {
  const [currentTip, setCurrentTip] = useState(0);

  const chessEducation = [
    {
      title: "Opening Principles",
      content: "Control the center, develop your pieces, and castle early for a strong start!",
      icon: BookOpen,
      color: "teen-green",
      category: "Strategy"
    },
    {
      title: "Tactical Patterns",
      content: "Learn pins, forks, and skewers to win material and create threats!",
      icon: Target,
      color: "teen-blue",
      category: "Tactics"
    },
    {
      title: "Endgame Mastery",
      content: "Master basic checkmates like Queen + King vs King for decisive victories!",
      icon: Crown,
      color: "teen-purple",
      category: "Endgames"
    },
    {
      title: "Mental Benefits",
      content: "Chess enhances critical thinking, patience, and decision-making skills!",
      icon: Brain,
      color: "teen-gold",
      category: "Benefits"
    }
  ];

  const skillProgression = [
    { level: "Beginner", description: "Learn piece movement and basic rules", icon: "🌱", skills: ["Piece Values", "Castling", "En Passant"] },
    { level: "Intermediate", description: "Master tactics and strategy", icon: "🌳", skills: ["Pins & Forks", "Pawn Structure", "King Safety"] },
    { level: "Advanced", description: "Develop positional understanding", icon: "🎯", skills: ["Weak Squares", "Piece Activity", "Pawn Breaks"] },
    { level: "Expert", description: "Refine endgame technique", icon: "👑", skills: ["Complex Endings", "Calculation", "Time Management"] }
  ];

  const communityValues = [
    { title: "Fair Play", description: "Respectful competition for everyone", icon: Heart, color: "teen-green" },
    { title: "Learning Focus", description: "Every game is a chance to improve", icon: Lightbulb, color: "teen-blue" },
    { title: "Inclusive Community", description: "Welcome to players of all levels", icon: Sparkles, color: "teen-purple" },
    { title: "Patient Growth", description: "Master chess one move at a time", icon: Clock, color: "teen-gold" }
  ];

  // Rotate educational tips every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % chessEducation.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-12">
      {/* Educational Tip Rotation */}
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-teen-green to-teen-blue bg-clip-text text-transparent">
            Chess Learning Hub 📚
          </h3>
          <p className="text-muted-foreground">
            Discover chess wisdom and improve your game!
          </p>
        </div>

        <Card className="bg-gradient-to-r from-background via-teen-green/5 to-teen-blue/5 border border-teen-green/20 max-w-2xl mx-auto">
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3">
                <div className={`p-3 bg-${chessEducation[currentTip].color}/20 rounded-full`}>
                  {React.createElement(chessEducation[currentTip].icon, { 
                    className: `w-6 h-6 text-${chessEducation[currentTip].color}` 
                  })}
                </div>
                <Badge variant="secondary" className={`bg-${chessEducation[currentTip].color}/10 text-${chessEducation[currentTip].color}`}>
                  {chessEducation[currentTip].category}
                </Badge>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-lg font-semibold text-foreground">
                  {chessEducation[currentTip].title}
                </h4>
                <p className="text-muted-foreground leading-relaxed">
                  {chessEducation[currentTip].content}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Skill Progression Path */}
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h3 className="text-xl font-bold text-foreground">
            Your Chess Journey 🚀
          </h3>
          <p className="text-sm text-muted-foreground">
            Progress through skill levels at your own pace
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {skillProgression.map((level, index) => (
            <Card 
              key={index} 
              className="text-center hover:scale-105 transition-all duration-300 cursor-pointer border-teen-blue/30 bg-teen-blue/5 hover:shadow-teen-glow"
            >
              <CardContent className="p-4 space-y-3">
                <div className="text-3xl">{level.icon}</div>
                <div>
                  <h4 className="font-bold text-teen-blue">{level.level}</h4>
                  <p className="text-xs text-muted-foreground mb-2">{level.description}</p>
            <div className="space-y-1">
              {level.skills.map((skill, skillIndex) => (
                <Badge key={skillIndex} variant="outline" className="text-xs">
                  {skill}
                </Badge>
              ))}
            </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Community Values */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {communityValues.map((value, index) => (
          <Card key={index} className={`text-center hover:scale-105 transition-all duration-300 border-${value.color}/30 bg-${value.color}/5 hover:bg-${value.color}/10`}>
            <CardContent className="p-4 space-y-3">
              <div className={`p-2 bg-${value.color}/20 rounded-lg inline-block`}>
                {React.createElement(value.icon, { 
                  className: `w-5 h-5 text-${value.color}` 
                })}
              </div>
              <div>
                <h4 className={`font-bold text-${value.color}`}>{value.title}</h4>
                <p className="text-xs text-muted-foreground">{value.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Call to Action */}
      <Card className="text-center bg-gradient-to-r from-teen-purple/10 to-teen-gold/10 border border-teen-purple/20">
        <CardContent className="p-6">
          <div className="space-y-3">
            <div className="text-3xl">♟️</div>
            <h4 className="text-lg font-bold text-foreground">
              Ready to Begin Your Chess Adventure?
            </h4>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Whether you're a complete beginner or looking to sharpen your skills, 
              ChessHatch provides the perfect environment for growth and fun!
            </p>
            <div className="flex justify-center gap-2 text-xs text-muted-foreground">
              <span>✨ Learn at your own pace</span>
              <span>•</span>
              <span>🎯 Practice with purpose</span>
              <span>•</span>
              <span>🏆 Celebrate every improvement</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}